import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import { Button, Checkbox, Dropdown, Field, Input, Option, RadioGroup, Radio, Text, Textarea, makeStyles, shorthands } from '@fluentui/react-components';
import {
  AA_SUPPORTED_LANGUAGES,
  CALL_QUEUE_NO_AGENT_ACTIONS,
  CALL_QUEUE_NO_AGENT_APPLY_TO,
  CALL_QUEUE_OVERFLOW_ACTIONS,
  CALL_QUEUE_ROUTING_METHODS,
  CALL_QUEUE_TIMEOUT_ACTIONS,
  type CallQueueWizardAnswers,
} from '@tvmf/shared';
import { api, ApiError } from '../api';
import { type Choice } from './records';
import { ResourceAccountCreateDialog } from './ResourceAccountCreateDialog';
import { UpnAutocomplete } from './UpnAutocomplete';
import { WizardShell, type WizardStep } from './WizardShell';

const useLocalStyles = makeStyles({
  flexWrapItemsEndGap8: { display: "flex", ...shorthands.gap('8px'), alignItems: "end", flexWrap: "wrap" },
  minW260: { minWidth: '260px' },
  minW320: { minWidth: '320px', flexGrow: 1 },
  minW220: { minWidth: '220px' },
  gridGap6: { display: "grid", ...shorthands.gap('6px') },
  flexGap6: { display: "flex", ...shorthands.gap('6px') },
  flex1: { flex: 1 },
  w120: { width: '120px' },
  mt8: { marginTop: '8px' },
  flexItemsEndGap8: { display: "flex", ...shorthands.gap('8px'), alignItems: "end" },
});


const LANGUAGE_CHOICES: Choice[] = AA_SUPPORTED_LANGUAGES.map((l) => ({ value: l.code, label: l.label }));

const ROUTING_LABELS: Record<(typeof CALL_QUEUE_ROUTING_METHODS)[number], string> = {
  Attendant: 'Ring everyone at once - first to answer gets it',
  Serial: 'Ring one person at a time, in order',
  RoundRobin: 'Spread calls evenly across the team (recommended)',
  LongestIdle: "Give the call to whoever's been free longest (recommended)",
};

// buildCallQueueWritable's overflow/timeout/no_agent_action fields (dto.ts)
// are typed as plain `action?: string`, not a literal union (their zod
// helper widens the parameter type deliberately) - CallQueueWizardAnswers
// mirrors that, so these lookup tables index by plain string too.
const OVERFLOW_LABELS: Record<string, string> = {
  DisconnectWithBusy: 'Play a busy signal and hang up',
  Forward: 'Send the call somewhere else',
  Voicemail: 'Send to voicemail',
  SharedVoicemail: 'Send to shared voicemail',
};
const TIMEOUT_LABELS: Record<string, string> = {
  Disconnect: 'Hang up',
  Forward: 'Send the call somewhere else',
  Voicemail: 'Send to voicemail',
  SharedVoicemail: 'Send to shared voicemail',
};
const NO_AGENT_LABELS: Record<string, string> = {
  Queue: 'Keep them waiting in the queue',
  Disconnect: 'Hang up',
  Forward: 'Send the call somewhere else',
  Voicemail: 'Send to voicemail',
  SharedVoicemail: 'Send to shared voicemail',
};
const APPLY_TO_LABELS: Record<(typeof CALL_QUEUE_NO_AGENT_APPLY_TO)[number], string> = {
  AllCalls: 'Everyone - calls already waiting too',
  NewCalls: 'Only new calls from now on',
};

/**
 * One overflow/timeout/no-agent exception answer - an action dropdown, plus
 * a "send it to" field only when the action needs one. Typed loosely over
 * plain `string` rather than parameterized on each call's own literal
 * union - the three call sites below each carry a differently-narrowed
 * `CallQueueWizardAnswers` sub-type, and threading that through a generic
 * component's inferred callback parameter fought TypeScript's inference
 * more than it was worth; each call site casts back to its own narrow type.
 */
function ActionAnswerEditor({
  actions,
  labels,
  value,
  onChange,
  tenantId,
  siteId,
}: {
  actions: readonly string[];
  labels: Record<string, string>;
  value: { action: string; forwardTo?: string; message?: string };
  onChange: (v: { action: string; forwardTo?: string; message?: string }) => void;
  tenantId: string;
  siteId: string;
}) {
  const ls = useLocalStyles();
  return (
    <div className={ls.flexWrapItemsEndGap8}>
      <Dropdown
        value={labels[value.action]}
        selectedOptions={[value.action]}
        onOptionSelect={(_, d) => onChange({ action: d.optionValue ?? value.action, forwardTo: undefined, message: value.message })}
        className={ls.minW260}
      >
        {actions.map((a) => (
          <Option key={a} value={a}>
            {labels[a]}
          </Option>
        ))}
      </Dropdown>
      {value.action === 'Forward' && (
        <Field label="Send it to (name, department, or number)">
          <UpnAutocomplete
            tenantId={tenantId}
            siteId={siteId}
            value={value.forwardTo ?? ''}
            onChange={(v) => onChange({ ...value, forwardTo: v })}
            className={ls.minW220}
          />
        </Field>
      )}
      {value.action === 'Voicemail' && (
        <Field label="Whose voicemail should take the message?">
          <UpnAutocomplete
            tenantId={tenantId}
            siteId={siteId}
            value={value.forwardTo ?? ''}
            onChange={(v) => onChange({ ...value, forwardTo: v })}
            placeholder="jane.doe@contoso.com"
            className={ls.minW220}
          />
        </Field>
      )}
      {value.action === 'SharedVoicemail' && (
        <Field label="Which shared mailbox or Microsoft 365 group?" hint="Your engineer will link the exact group.">
          <Input value={value.forwardTo ?? ''} onChange={(_, d) => onChange({ ...value, forwardTo: d.value })} placeholder="Support team" className={ls.minW220} />
        </Field>
      )}
      {(value.action === 'Disconnect' || value.action === 'DisconnectWithBusy') && (
        <Field label="Message to play before hanging up (optional)" hint="Read out to the caller by Teams, then the call ends." className={ls.minW320}>
          <Textarea
            value={value.message ?? ''}
            onChange={(_, d) => onChange({ ...value, message: d.value })}
            maxLength={1000}
            resize="vertical"
            placeholder="Sorry, we can't take your call right now. Please call back later."
          />
        </Field>
      )}
    </div>
  );
}

/** The review line's note of a message played before hanging up. */
const said = (a: { action: string; message?: string }) =>
  (a.action === 'Disconnect' || a.action === 'DisconnectWithBusy') && a.message?.trim() ? `, after saying "${a.message.trim().length > 60 ? `${a.message.trim().slice(0, 60)}…` : a.message.trim()}"` : '';

const DEFAULT_ANSWERS: CallQueueWizardAnswers = {
  languageId: 'en-US',
  agents: [],
  routingMethod: 'RoundRobin',
  presenceBasedRouting: true,
  agentAlertTime: 20,
  overflowThreshold: 50,
  overflow: { action: 'DisconnectWithBusy' },
  timeoutThreshold: 1800,
  timeout: { action: 'Disconnect' },
  noAgents: { action: 'Queue' },
  noAgentsApplyTo: 'AllCalls',
};

/** An existing wizard-captured Call flows row, reopened for editing - see SiteWorkspace.tsx's onEditRow. */
export interface CallQueueWizardEditing {
  id: string;
  name: string;
  answers: CallQueueWizardAnswers;
  resourceAccountId: string | null;
}

export function CallQueueWizard({
  open,
  onOpenChange,
  base,
  tenantId,
  siteId,
  resourceAccountChoices,
  onCreated,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  base: string;
  tenantId: string;
  siteId: string;
  resourceAccountChoices: Choice[];
  /** Called after a successful save. Receives the saved discovery_flows row when the caller needs it (e.g. TargetPicker's nested "Set up a new call queue" flow linking the new row as a target) - existing zero-arg callers are unaffected. */
  onCreated: (row?: { id: string; name: string }) => void;
  /** Editing an already-saved wizard capture instead of creating a new one - PATCHes the existing flow row and shows "Save changes" instead of "Create". Render with `key={editing.id}` so each row gets its own fresh state. */
  editing?: CallQueueWizardEditing;
}) {
  const ls = useLocalStyles();
  const [name, setName] = useState(editing?.name ?? '');
  const [answers, setAnswers] = useState<CallQueueWizardAnswers>(editing?.answers ?? DEFAULT_ANSWERS);
  const [resourceAccountId, setResourceAccountId] = useState(editing?.resourceAccountId ?? '');
  const [raDialogOpen, setRaDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        ...(editing ? {} : { site_id: siteId, kind: 'call_queue' }),
        name,
        description: `Captured via the Call Queue wizard - ${answers.agents.length} agent(s), ${ROUTING_LABELS[answers.routingMethod]}`,
        wizard_answers: answers,
        wizard_version: 1,
        resource_account_id: resourceAccountId || null,
      });
      return editing
        ? api<{ id: string; name: string }>(`${base}/flows/${editing.id}`, { method: 'PATCH', body })
        : api<{ id: string; name: string }>(`${base}/flows`, { method: 'POST', body });
    },
    onSuccess: (row) => {
      onOpenChange(false);
      if (!editing) {
        setName('');
        setAnswers(DEFAULT_ANSWERS);
        setResourceAccountId('');
      }
      onCreated(row);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Save failed'),
  });

  const setAgent = (i: number, v: string) => setAnswers((a) => ({ ...a, agents: a.agents.map((x, idx) => (idx === i ? v : x)) }));

  const steps: WizardStep[] = [
    {
      key: 'basics',
      title: 'Basics',
      validate: () => (!name.trim() ? 'Give this call queue a name.' : null),
      content: (
        <>
          <Field label="What do you want to call this call queue?" required>
            <Input value={name} placeholder="Support Team" onChange={(_, d) => setName(d.value)} />
          </Field>
          <Field label="What language should the automated voice speak?">
            <Dropdown
              value={LANGUAGE_CHOICES.find((c) => c.value === answers.languageId)?.label ?? ''}
              selectedOptions={[answers.languageId]}
              onOptionSelect={(_, d) => setAnswers((a) => ({ ...a, languageId: d.optionValue ?? a.languageId }))}
            >
              {LANGUAGE_CHOICES.map((c) => (
                <Option key={c.value} value={c.value}>
                  {c.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </>
      ),
    },
    {
      key: 'agents',
      title: 'Who answers',
      content: (
        <>
          <Text block>Who should answer these calls? Add each person's name or email.</Text>
          <div className={ls.gridGap6}>
            {answers.agents.map((a, i) => (
              <div key={i} className={ls.flexGap6}>
                <UpnAutocomplete
                  tenantId={tenantId}
                  siteId={siteId}
                  value={a}
                  onChange={(v) => setAgent(i, v)}
                  placeholder="jane.doe@contoso.com"
                  className={ls.flex1}
                />
                <Button
                  icon={<DeleteRegular />}
                  appearance="subtle"
                  aria-label="Remove"
                  onClick={() => setAnswers((ans) => ({ ...ans, agents: ans.agents.filter((_, idx) => idx !== i) }))}
                />
              </div>
            ))}
            <Button
              size="small"
              icon={<AddRegular />}
              disabled={answers.agents.length >= 50}
              onClick={() => setAnswers((a) => ({ ...a, agents: [...a.agents, ''] }))}
            >
              Add person
            </Button>
          </div>
          <Field label="How should incoming calls be shared among them?">
            <Dropdown
              value={ROUTING_LABELS[answers.routingMethod]}
              selectedOptions={[answers.routingMethod]}
              onOptionSelect={(_, d) => setAnswers((a) => ({ ...a, routingMethod: (d.optionValue ?? a.routingMethod) as typeof a.routingMethod }))}
            >
              {CALL_QUEUE_ROUTING_METHODS.map((m) => (
                <Option key={m} value={m}>
                  {ROUTING_LABELS[m]}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Checkbox
            checked={answers.presenceBasedRouting}
            label="Only ring people who are free (not already on a call or set to do not disturb)"
            onChange={(_, d) => setAnswers((a) => ({ ...a, presenceBasedRouting: !!d.checked }))}
          />
          <Field label="How long should we try each person before moving on? (seconds)">
            <Input
              type="number"
              value={String(answers.agentAlertTime)}
              onChange={(_, d) => setAnswers((a) => ({ ...a, agentAlertTime: Number(d.value) || a.agentAlertTime }))}
              className={ls.w120}
            />
          </Field>
        </>
      ),
    },
    {
      key: 'overflow',
      title: 'Too many calls waiting',
      content: (
        <>
          <Field label="How many calls should be allowed to wait at once?">
            <Input
              type="number"
              value={String(answers.overflowThreshold)}
              onChange={(_, d) => setAnswers((a) => ({ ...a, overflowThreshold: Number(d.value) || 0 }))}
              className={ls.w120}
            />
          </Field>
          <Text block>When that limit is reached, what should happen to the extra calls?</Text>
          <ActionAnswerEditor
            actions={CALL_QUEUE_OVERFLOW_ACTIONS}
            labels={OVERFLOW_LABELS}
            value={answers.overflow}
            onChange={(v) => setAnswers((a) => ({ ...a, overflow: v as CallQueueWizardAnswers['overflow'] }))}
            tenantId={tenantId}
            siteId={siteId}
          />
        </>
      ),
    },
    {
      key: 'timeout',
      title: 'Waiting too long',
      content: (
        <>
          <Field label="How long should someone wait before we do something different? (seconds)">
            <Input
              type="number"
              value={String(answers.timeoutThreshold)}
              onChange={(_, d) => setAnswers((a) => ({ ...a, timeoutThreshold: Number(d.value) || 0 }))}
              className={ls.w120}
            />
          </Field>
          <Text block>What should happen then?</Text>
          <ActionAnswerEditor
            actions={CALL_QUEUE_TIMEOUT_ACTIONS}
            labels={TIMEOUT_LABELS}
            value={answers.timeout}
            onChange={(v) => setAnswers((a) => ({ ...a, timeout: v as CallQueueWizardAnswers['timeout'] }))}
            tenantId={tenantId}
            siteId={siteId}
          />
        </>
      ),
    },
    {
      key: 'no-agents',
      title: 'Nobody available',
      content: (
        <>
          <Text block>What should happen if nobody's logged in or available to take calls?</Text>
          <ActionAnswerEditor
            actions={CALL_QUEUE_NO_AGENT_ACTIONS}
            labels={NO_AGENT_LABELS}
            value={answers.noAgents}
            onChange={(v) => setAnswers((a) => ({ ...a, noAgents: v as CallQueueWizardAnswers['noAgents'] }))}
            tenantId={tenantId}
            siteId={siteId}
          />
          <Field label="Should this apply to calls already waiting too, or just new calls?">
            <RadioGroup
              value={answers.noAgentsApplyTo}
              onChange={(_, d) => setAnswers((a) => ({ ...a, noAgentsApplyTo: d.value as typeof a.noAgentsApplyTo }))}
            >
              {CALL_QUEUE_NO_AGENT_APPLY_TO.map((v) => (
                <Radio key={v} value={v} label={APPLY_TO_LABELS[v]} />
              ))}
            </RadioGroup>
          </Field>
        </>
      ),
    },
    {
      key: 'review',
      title: 'Review',
      content: (
        <>
          <Text size={200} block>
            {answers.agents.filter(Boolean).length} agent(s) · {ROUTING_LABELS[answers.routingMethod]}
          </Text>
          <Text size={200} block>
            Too many calls waiting ({answers.overflowThreshold}+): {OVERFLOW_LABELS[answers.overflow.action]}{said(answers.overflow)}
          </Text>
          <Text size={200} block>
            Waiting too long ({answers.timeoutThreshold}s+): {TIMEOUT_LABELS[answers.timeout.action]}{said(answers.timeout)}
          </Text>
          <Text size={200} block>
            Nobody available: {NO_AGENT_LABELS[answers.noAgents.action]}{said(answers.noAgents)} ({APPLY_TO_LABELS[answers.noAgentsApplyTo]})
          </Text>
          <Text size={200} block className={ls.mt8}>
            Note: Call queues don't have their own after-hours/holiday handling in Teams - put an Auto Attendant in front of
            this queue if you need that.
          </Text>
          <Field label="Resource account that answers this (optional - can be set later)">
            <div className={ls.flexItemsEndGap8}>
              <Dropdown
                value={resourceAccountChoices.find((c) => c.value === resourceAccountId)?.label ?? '— not yet known —'}
                selectedOptions={resourceAccountId ? [resourceAccountId] : []}
                onOptionSelect={(_, d) => setResourceAccountId(d.optionValue ?? '')}
                className={ls.minW220}
              >
                <Option value="">— not yet known —</Option>
                {resourceAccountChoices.map((c) => (
                  <Option key={c.value} value={c.value}>
                    {c.label}
                  </Option>
                ))}
              </Dropdown>
              <Button size="small" onClick={() => setRaDialogOpen(true)}>
                Set up a new resource account
              </Button>
            </div>
          </Field>
          <ResourceAccountCreateDialog
            open={raDialogOpen}
            onOpenChange={setRaDialogOpen}
            base={base}
            tenantId={tenantId}
            siteId={siteId}
            kind="call_queue"
            onCreated={(row) => {
              if (row) setResourceAccountId(row.id);
            }}
          />
        </>
      ),
    },
  ];

  return (
    <WizardShell
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        // Reset on Cancel too, not just on a successful save - the "New"
        // instance stays mounted the whole time (WizardTiles just toggles
        // `open`), so leftover text/answers from an abandoned attempt would
        // otherwise still be there next time this tile is opened. Not
        // needed in edit mode - that instance is remounted fresh (key={editing.id})
        // every time a different row is opened for editing.
        if (!o && !editing) {
          setError(null);
          setName('');
          setAnswers(DEFAULT_ANSWERS);
          setResourceAccountId('');
        }
      }}
      title={editing ? `Edit "${editing.name}"` : 'New Call Queue'}
      steps={steps}
      completing={save.isPending}
      completeLabel={editing ? 'Save changes' : 'Create'}
      error={error}
      onComplete={() => {
        setError(null);
        save.mutate();
      }}
    />
  );
}
